import ImageOptimization from '@/services/image_optimization';

export interface ImageWrapperProps extends React.ImgHTMLAttributes<HTMLImageElement> {
    src: string;
    mobile?: string;
    ariaLabel?: string;
    ariaHidden?: boolean;
    loadingStrategy?: 'eager' | 'lazy';
    fetchPriority?: 'auto' | 'high' | 'low';
    optimizedWidth?: number;
    optimizedHeight?: number;
    optimizedMobileWidth?: number;
    optimizedMobileHeight?: number;
    maxWidth?: number;
    skipOptimization?: boolean;
}

const getResponsiveDimensions = (desktopWidth: number, desktopHeight: number) => {
    const aspectRatio = desktopHeight / desktopWidth;

    return {
        desktop: { width: desktopWidth, height: desktopHeight },
        mobile: { width: 768, height: Math.round(768 * aspectRatio) },
    };
};

export function ImageWrapper({
    src,
    alt,
    className,
    style,
    id,
    loadingStrategy = 'lazy',
    fetchPriority = 'auto',
    maxWidth,
    optimizedWidth,
    optimizedHeight,
    optimizedMobileHeight,
    optimizedMobileWidth,
    skipOptimization = false,
    mobile,
    ...rest
}: ImageWrapperProps) {
    if (!src && !mobile) {
        return <></>;
    }

    let optimizedDesktopSrc, optimizedMobileSrc;

    if (optimizedWidth !== undefined && optimizedHeight !== undefined) {
        if (optimizedMobileHeight && optimizedMobileWidth) {
            optimizedDesktopSrc = ImageOptimization({
                src: src,
                width: optimizedWidth,
                height: optimizedHeight,
                maxWidth,
                skipOptimization: skipOptimization,
            });

            optimizedMobileSrc = ImageOptimization({
                src: mobile || src,
                width: optimizedMobileWidth,
                height: optimizedMobileHeight,
                maxWidth,
                skipOptimization: skipOptimization,
            });
        } else {
            const dimensions = getResponsiveDimensions(optimizedWidth, optimizedHeight);
            const mobileImageSrc = mobile || src;

            optimizedDesktopSrc = ImageOptimization({
                src: src,
                width: dimensions.desktop.width,
                height: dimensions.desktop.height,
                maxWidth,
                skipOptimization: skipOptimization,
            });

            optimizedMobileSrc = ImageOptimization({
                src: mobileImageSrc,
                width: dimensions.mobile.width,
                height: dimensions.mobile.height,
                maxWidth,
                skipOptimization: skipOptimization,
            });
        }
    } else {
        const fallbackSrc = ImageOptimization({
            src: src,
            width: optimizedWidth,
            height: optimizedHeight,
            maxWidth,
            skipOptimization: skipOptimization,
        });

        optimizedDesktopSrc = fallbackSrc;
        optimizedMobileSrc = fallbackSrc;
    }

    return (
        <picture>
            {optimizedDesktopSrc && <source media="(min-width: 1024px)" srcSet={`${optimizedDesktopSrc}`} />}
            {optimizedMobileSrc && <source media="(max-width: 1023px)" srcSet={`${optimizedMobileSrc}`} />}
            <img
                id={id}
                alt={alt}
                className={className}
                style={style}
                fetchPriority={fetchPriority}
                loading={loadingStrategy}
                aria-label={rest['aria-label'] || rest.ariaLabel}
                aria-hidden={!!rest['aria-hidden'] || rest.ariaHidden}
            />
        </picture>
    );
}
export interface BackgroundImageProps {
    src?: string;
    width?: number;
    height?: number;
}

export const getBackgroundImage = ({ src, width, height }: BackgroundImageProps) => {
    return src && `url("${ImageOptimization({ src, width, height })}")`;
};
